import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/SOP-api";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { jwtDecode } from "jwt-decode";
import { IconButton, Snackbar, Alert } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function LeaveApplicationForm() {
  const [userInfo, setUserInfo] = useState({ email: "", employeeId: "" });
  const [formData, setFormData] = useState({
    leaveType: "",
    leaveDuration: "",
    date: "",
    startTime: "",
    endTime: "",
    fromDate: "",
    toDate: "",
    numberOfDays: "",
    reason: "",
    session: "AM",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  const cardStyle = {
    maxWidth: 640,
    margin: "0 auto",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(12px)",
    borderRadius: 16,
    boxShadow:
      "0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  };

  const labelStyle = {
    display: "block",
    marginBottom: 8,
    fontWeight: 600,
    color: "#334155",
    fontSize: 14,
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    fontSize: 14,
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.04)",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    outline: "none",
  };

  const today = dayjs().format("YYYY-MM-DD");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hourInt = parseInt(hours, 10);
    const ampm = hourInt >= 12 ? "PM" : "AM";
    const displayHour = hourInt % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const validateDate = (date, leaveType) => {
    if (!date) return false;
    const selected = dayjs(date);
    const current = dayjs(today);

    if (selected.isBefore(current, "day")) return false;

    switch (leaveType) {
      case "casualLeave":
        return selected.isAfter(dayjs(tomorrow), "day");
      case "sickLeave":
        return (
          selected.isSame(current, "day") ||
          selected.isSame(dayjs(tomorrow), "day")
        );
      case "compensatoryOff":
        return (
          selected.isSame(current, "day") || selected.isAfter(current, "day")
        );
      default:
        return true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date ? dayjs(date).format("YYYY-MM-DD") : "",
    }));
  };

  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const from = dayjs(formData.fromDate);
      const to = dayjs(formData.toDate);
      if (to.isValid() && from.isValid() && to >= from) {
        const days = to.diff(from, "day") + 1;
        setFormData((prev) => ({ ...prev, numberOfDays: days }));
      }
    }
  }, [formData.fromDate, formData.toDate]);

  // Decode token when component mounts
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserInfo({
          email: decoded.email,
          employeeId: decoded.employeeId,
        });
      } catch (error) {
        console.error("Error decoding token:", error);
        setSnackbar({
          open: true,
          message: "Error loading user information",
          severity: "error",
        });
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const submissionData = {
        leaveType: formData.leaveType,
        reason: formData.reason,
      };

      if (formData.leaveType === "compensatoryOff") {
        if (!validateDate(formData.date, formData.leaveType)) {
          throw new Error("Invalid date for compensatory off.");
        }
        submissionData.date = formData.date;
      } else {
        submissionData.leaveDuration = formData.leaveDuration;

        switch (formData.leaveDuration) {
          case "oneHour":
          case "twoHours":
            if (!validateDate(formData.date, formData.leaveType)) {
              throw new Error("Invalid date for hourly leave.");
            }
            submissionData.date = formData.date;
            submissionData.startTime = formatTime(formData.startTime);
            submissionData.endTime = formatTime(formData.endTime);
            break;
          case "halfday":
            if (!validateDate(formData.date, formData.leaveType)) {
              throw new Error("Invalid date for half day leave.");
            }
            submissionData.date = formData.date;
            submissionData.session = formData.session;
            break;
          case "oneDay":
            if (!validateDate(formData.date, formData.leaveType)) {
              throw new Error("Invalid date for one day leave.");
            }
            submissionData.date = formData.date;
            break;
          case "morethanoneDay":
            if (
              !validateDate(formData.fromDate, formData.leaveType) ||
              !validateDate(formData.toDate, formData.leaveType)
            ) {
              throw new Error("Invalid date range for multi-day leave.");
            }
            if (dayjs(formData.toDate).isBefore(dayjs(formData.fromDate))) {
              throw new Error("End date cannot be before start date.");
            }
            submissionData.fromDate = formData.fromDate;
            submissionData.toDate = formData.toDate;
            submissionData.numberOfDays = formData.numberOfDays;
            break;
          default:
            throw new Error("Please select a valid leave duration.");
        }
      }

      const response = await axios.post("/leave/apply", submissionData, config);

      setSnackbar({
        open: true,
        message:
          response.data.message || "Leave application submitted successfully!",
        severity: "success",
      });

      // Reset form after successful submission
      setFormData({
        leaveType: "",
        leaveDuration: "",
        date: "",
        startTime: "",
        endTime: "",
        fromDate: "",
        toDate: "",
        numberOfDays: "",
        reason: "",
        session: "AM",
      });
    } catch (err) {
      const errorMsg =
        err.response?.data?.error || err.message || "Submission failed.";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDateInput = (field, label) => {
    let minDate, maxDate;
    const currentDate = dayjs();

    switch (formData.leaveType) {
      case "casualLeave":
        minDate = currentDate.add(2, "day").toDate();
        break;
      case "sickLeave":
        minDate = currentDate.toDate();
        maxDate = currentDate.add(1, "day").toDate();
        break;
      case "compensatoryOff":
        minDate = currentDate.toDate();
        break;
      default:
        minDate = currentDate.toDate();
    }

    const isSunday = (date) => dayjs(date).day() === 0;

    return (
      <div style={{ marginBottom: 24 }}>
        <label htmlFor={field} style={labelStyle}>
          {label}
        </label>
        <DatePicker
          id={field}
          selected={formData[field] ? new Date(formData[field]) : null}
          onChange={(date) => handleDateChange(date, field)}
          minDate={minDate}
          maxDate={maxDate}
          filterDate={(date) => !isSunday(date)}
          dayClassName={(date) => (isSunday(date) ? "sunday" : undefined)}
          placeholderText="Select date"
          dateFormat="yyyy-MM-dd"
          style={inputStyle}
          wrapperClassName="date-picker"
          required
        />
      </div>
    );
  };

  return (
    <div style={pageStyle}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ marginTop: "60px" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 32 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Leave Application
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Apply for casual, sick, or compensatory leave
        </p>
      </div>

      <div style={cardStyle}>
        <style>
          {`
          @keyframes spin { to { transform: rotate(360deg); } }
          .sunday { color: #e53e3e; font-weight: bold; }
          .date-picker input { width: 100%; padding: 12px 14px; border-radius: 10px; border: 1px solid #e2e8f0; font-size: 14px; outline: none; }
          .date-picker input:focus { border-color: #667eea; box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2); }
          .react-datepicker { font-family: 'Inter', sans-serif; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 10px 40px -10px rgba(0,0,0,0.12); }
          .react-datepicker__header { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-bottom: 1px solid #e2e8f0; }
          .react-datepicker__day--selected { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; }
          .react-datepicker__day--selected:hover { opacity: 0.9; }
        `}
        </style>

        <div style={{ padding: "28px 32px 32px" }}>
          <div
            style={{
              marginBottom: 28,
              padding: "20px 24px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: 12,
              border: "1px solid #e2e8f0",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: "1rem",
                fontWeight: 600,
                color: "#334155",
                textAlign: "center",
              }}
            >
              Applicant Information
            </h3>
            <p style={{ margin: "4px 0", fontSize: 14, color: "#64748b" }}>
              <strong style={{ color: "#334155" }}>Email:</strong>{" "}
              {userInfo.email || "Loading..."}
            </p>
            <p style={{ margin: "4px 0", fontSize: 14, color: "#64748b" }}>
              <strong style={{ color: "#334155" }}>Employee ID:</strong>{" "}
              {userInfo.employeeId || "Loading..."}
            </p>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
              <div>
                <label style={labelStyle}>Leave Type</label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleChange}
                  required
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    backgroundImage:
                      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    backgroundSize: "1em",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.04)";
                  }}
                >
                  <option value="">Select Leave Type</option>
                  <option value="casualLeave">Casual Leave</option>
                  <option value="sickLeave">Sick Leave</option>
                  <option value="compensatoryOff">Compensatory Off</option>
                </select>
              </div>
            </div>

            {formData.leaveType && formData.leaveType !== "compensatoryOff" && (
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Leave Duration</label>
                <select
                  name="leaveDuration"
                  value={formData.leaveDuration}
                  onChange={handleChange}
                  required
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    backgroundImage:
                      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    backgroundSize: "1em",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.04)";
                  }}
                >
                  <option value="">Select Duration</option>
                  {formData.leaveType === "sickLeave" && (
                    <>
                      <option value="oneDay">One Day</option>
                      <option value="halfday">Half Day</option>
                      <option value="oneHour">One Hour</option>
                      <option value="twoHours">Two Hours</option>
                    </>
                  )}
                  {formData.leaveType === "casualLeave" && (
                    <>
                      <option value="oneDay">One Day</option>
                      <option value="morethanoneDay">More than 1 Day</option>
                    </>
                  )}
                </select>
              </div>
            )}

            {formData.leaveType === "compensatoryOff" &&
              renderDateInput("date", "Select Date")}

            {["oneDay", "halfday", "oneHour", "twoHours"].includes(
              formData.leaveDuration
            ) && renderDateInput("date", "Select Date")}

            {formData.leaveDuration === "halfday" && (
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Session</label>
                <select
                  name="session"
                  value={formData.session}
                  onChange={handleChange}
                  required
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    backgroundImage:
                      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    backgroundSize: "1em",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.04)";
                  }}
                >
                  <option value="AM">Morning</option>
                  <option value="PM">Afternoon</option>
                </select>
              </div>
            )}

            {["oneHour", "twoHours"].includes(formData.leaveDuration) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                <div>
                  <label style={labelStyle}>Start Time</label>
                  <input
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#667eea";
                      e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.2)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.04)";
                    }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>End Time</label>
                  <input
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#667eea";
                      e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.2)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.04)";
                    }}
                  />
                </div>
              </div>
            )}

            {formData.leaveDuration === "morethanoneDay" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 24 }}>
                  <div>
                    <label style={labelStyle}>From Date</label>
                    <DatePicker
                      selected={formData.fromDate ? new Date(formData.fromDate) : null}
                      onChange={(date) => handleDateChange(date, "fromDate")}
                      minDate={dayjs().add(2, "day").toDate()}
                      filterDate={(date) => dayjs(date).day() !== 0}
                      dayClassName={(date) => (dayjs(date).day() === 0 ? "sunday" : undefined)}
                      placeholderText="Select date"
                      dateFormat="yyyy-MM-dd"
                      style={inputStyle}
                      wrapperClassName="date-picker"
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>To Date</label>
                    <DatePicker
                      selected={formData.toDate ? new Date(formData.toDate) : null}
                      onChange={(date) => handleDateChange(date, "toDate")}
                      minDate={formData.fromDate ? new Date(formData.fromDate) : dayjs().add(2, "day").toDate()}
                      filterDate={(date) => dayjs(date).day() !== 0}
                      dayClassName={(date) => (dayjs(date).day() === 0 ? "sunday" : undefined)}
                      placeholderText="Select date"
                      dateFormat="yyyy-MM-dd"
                      style={inputStyle}
                      wrapperClassName="date-picker"
                      required
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Number of Days</label>
                    <input
                      type="number"
                      name="numberOfDays"
                      value={formData.numberOfDays}
                      readOnly
                      style={{ ...inputStyle, backgroundColor: "#f8fafc" }}
                    />
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: 32 }}>
              <label style={labelStyle}>Reason</label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                required
                style={{
                  ...inputStyle,
                  minHeight: 120,
                  resize: "vertical",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#667eea";
                  e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.2)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.04)";
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%",
                padding: "16px 24px",
                background: isSubmitting
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "opacity 0.2s ease, transform 0.1s ease",
                boxShadow: isSubmitting
                  ? "none"
                  : "0 4px 14px 0 rgba(102, 126, 234, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              onMouseOver={(e) =>
                !isSubmitting && (e.currentTarget.style.opacity = "0.92")
              }
              onMouseOut={(e) =>
                !isSubmitting && (e.currentTarget.style.opacity = "1")
              }
            >
              {isSubmitting ? (
                <>
                  <svg
                    style={{ width: 20, height: 20, animation: "spin 1s linear infinite" }}
                    viewBox="0 0 24 24"
                  >
                    <path fill="currentColor" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                  </svg>
                  Processing...
                </>
              ) : (
                "Submit Application"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
